import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { WelcomeStep } from './steps/WelcomeStep';
import { TermsStep } from './steps/TermsStep';
import { SportsbooksStep } from './steps/SportsbooksStep';
import { BankrollStep } from './steps/BankrollStep';
import { PricingStep } from './steps/PricingStep';
import { TourStep } from './steps/TourStep';
import './Onboarding.css';

export const OnboardingFlow = (props) => {
	const { bankroll, setBankroll, filtersOpen, setFiltersOpen, setIsSessionExpired } = props;
	const { user, getAccessTokenSilently } = useAuth0();

	const [stepIndex, setStepIndex] = useState(0);
	const [stepKeys, setStepKeys] = useState(null);

	const stepIndexRef = useRef(stepIndex);
	useEffect(() => {
		stepIndexRef.current = stepIndex;
	}, [stepIndex]);

	const totalSteps = stepKeys ? stepKeys.length : 0;
	const currentKey = stepKeys ? stepKeys[Math.min(stepIndex, totalSteps - 1)] : null;

	useEffect(() => {
		let cancelled = false;
		let initialized = false;

		const fetchData = async () => {
			try {
				const token = await getAccessTokenSilently();
				const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/user`, user, {
					headers: { Authorization: `Bearer ${token}` },
				});
				if (cancelled) return;

				if (!initialized) {
					initialized = true;
					const includeTerms = response.data?.terms_completed !== 'true';
					const keys = ['welcome'];
					if (includeTerms) keys.push('terms');
					keys.push('sportsbooks', 'bankroll', 'pricing', 'tour');
					setStepKeys(keys);
				}
			} catch (error) {
				console.error('OnboardingFlow user fetch failed:', error);
				if (error.response?.status >= 400 && setIsSessionExpired) {
					setIsSessionExpired(true);
					window.localStorage.clear();
				}
			}
		};

		fetchData();
		const intervalId = setInterval(fetchData, 7000);
		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	const goNext = () => setStepIndex((i) => Math.min(i + 1, totalSteps - 1));
	const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

	const sharedProps = {
		stepIndex,
		totalSteps,
		onNext: goNext,
		onBack: stepIndex > 0 ? goBack : undefined,
	};

	let stepEl = null;
	if (stepKeys) {
		switch (currentKey) {
			case 'welcome':
				stepEl = <WelcomeStep {...sharedProps} />;
				break;
			case 'terms':
				stepEl = <TermsStep {...sharedProps} />;
				break;
			case 'sportsbooks':
				stepEl = <SportsbooksStep {...sharedProps} />;
				break;
			case 'bankroll':
				stepEl = <BankrollStep {...sharedProps} bankroll={bankroll} setBankroll={setBankroll} />;
				break;
			case 'pricing':
				stepEl = <PricingStep {...sharedProps} />;
				break;
			case 'tour':
				stepEl = <TourStep {...sharedProps} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />;
				break;
			default:
				stepEl = <WelcomeStep {...sharedProps} />;
		}
	}

	return (
		<div className="flex flex-col flex-1 min-h-0 w-full overflow-hidden">
			<div className="flex flex-col flex-1 min-h-0 overflow-hidden">{stepEl}</div>
		</div>
	);
};
